import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '../../services/api';
import './ToneArchetypeStep.css';

/**
 * Onboarding step that lets the user pick their one-time brand voice archetype.
 * Fetches the list from GET /api/v1/tone-archetypes and POSTs the selection to
 * POST /api/v1/onboarding/tone, then calls onComplete() + onNext().
 */
function ToneArchetypeStep({ onComplete, onNext }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith('en') ? 'en' : 'it';

  const [archetypes, setArchetypes] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Mount-only fetch. Do NOT depend on ``t``: the function reference
    // returned by ``useTranslation`` changes whenever i18next finishes
    // loading the locale files asynchronously (or the user switches
    // language), and a refetch on every ``t`` change races with the
    // first request and can leave the component stuck on
    // ``loading=true`` when responses arrive out of order. ``t`` is
    // used only inside the catch branch — a slightly stale closure
    // there is harmless because i18next resolves keys at call time,
    // not at closure creation.
    let cancelled = false;
    const fetchArchetypes = async () => {
      try {
        const response = await apiClient.get('/api/v1/tone-archetypes');
        if (!cancelled) setArchetypes(response.data);
      } catch (err) {
        console.error('Failed to fetch archetypes:', err);
        if (!cancelled) setError(t('onboarding.tone.error'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchArchetypes();
    return () => {
      // Guard against state updates after unmount or remount during
      // language switch.
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConfirm = async () => {
    if (!selectedId) return;

    try {
      setSubmitting(true);
      setError(null);
      await apiClient.post('/api/v1/onboarding/tone', { archetype_id: selectedId });
      onComplete();
      onNext();
    } catch (err) {
      console.error('Failed to save tone archetype:', err);
      setError(err.response?.data?.detail || t('onboarding.tone.error'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="tone-step">
        <div className="tone-loading">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="tone-step">
      <div className="tone-step-header">
        <h2>{t('onboarding.tone.title')}</h2>
        <p className="tone-step-subtitle">{t('onboarding.tone.subtitle')}</p>
      </div>

      {error && <div className="tone-step-error">{error}</div>}

      <div className="archetype-grid">
        {archetypes.map((archetype) => (
          <button
            key={archetype.id}
            type="button"
            className={`archetype-card ${selectedId === archetype.id ? 'selected' : ''}`}
            onClick={() => setSelectedId(archetype.id)}
          >
            <span className="archetype-icon">{archetype.icon}</span>
            <span className="archetype-label">{archetype[`label_${lang}`]}</span>
            <span className="archetype-tagline">{archetype[`tagline_${lang}`]}</span>
            <span className="archetype-description">{archetype[`short_description_${lang}`]}</span>
          </button>
        ))}
      </div>

      <p className="tone-step-notice">{t('onboarding.tone.notice')}</p>

      <button
        className="btn btn-primary btn-large"
        onClick={handleConfirm}
        disabled={!selectedId || submitting}
      >
        {submitting ? t('onboarding.tone.confirming') : t('onboarding.tone.confirmButton')}
      </button>
    </div>
  );
}

export default ToneArchetypeStep;
